import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiDocumentsAPI, customersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Upload, FileText, FileSpreadsheet, FileImage, 
  Package, ShoppingCart, CheckCircle, Sparkles, RefreshCw
} from 'lucide-react';
import { DraftPartsTab } from '../components/documents/DraftPartsTab';
import { DraftOrdersTab } from '../components/documents/DraftOrdersTab';
import { DocumentUploadTab } from '../components/documents/DocumentUploadTab';
import { DocumentsListTab } from '../components/documents/DocumentsListTab';
import { SmartCopyDialog } from '../components/documents/SmartCopyDialog';
import { QuickCreatePartDialog } from '../components/documents/QuickCreatePartDialog';

export default function DocumentProcessingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Documents list state
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  // Draft parts state
  const [draftParts, setDraftParts] = useState([]);
  const [loadingDraftParts, setLoadingDraftParts] = useState(false);
  const [editingDraftPart, setEditingDraftPart] = useState(null);
  
  // Draft orders state
  const [draftOrders, setDraftOrders] = useState([]);
  const [loadingDraftOrders, setLoadingDraftOrders] = useState(false);
  
  // Quick create part dialog
  const [quickCreateDialog, setQuickCreateDialog] = useState(false);
  const [quickCreateData, setQuickCreateData] = useState({});
  const [quickCreateItemId, setQuickCreateItemId] = useState(null);
  const [selectedDraftOrder, setSelectedDraftOrder] = useState(null);
  
  // Smart Copy dialog state
  const [smartCopyDialog, setSmartCopyDialog] = useState(false);
  const [smartCopyDraft, setSmartCopyDraft] = useState(null);
  const [smartCopySuggestions, setSmartCopySuggestions] = useState([]);
  const [selectedFlowToCopy, setSelectedFlowToCopy] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Customers for matching
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    loadDocuments();
    loadDraftParts();
    loadDraftOrders();
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await customersAPI.list({ is_active: true });
      setCustomers(res.data?.items || res.data || []);
    } catch (err) {}
  };

  const loadDocuments = async () => {
    setLoadingDocs(true);
    try {
      const res = await aiDocumentsAPI.listDocuments();
      setDocuments(res.data?.documents || []);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadDraftParts = async () => {
    setLoadingDraftParts(true);
    try {
      const res = await aiDocumentsAPI.listDraftParts({ status: 'pending' });
      setDraftParts(res.data?.drafts || []);
    } catch (err) {
      toast.error('Failed to load draft parts');
    } finally {
      setLoadingDraftParts(false);
    }
  };

  const loadDraftOrders = async () => {
    setLoadingDraftOrders(true);
    try {
      const res = await aiDocumentsAPI.listDraftOrders({ status: 'pending' });
      setDraftOrders(res.data?.drafts || []);
    } catch (err) {
      toast.error('Failed to load draft orders');
    } finally {
      setLoadingDraftOrders(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/pdf', 'image/jpeg', 'image/png', 'image/webp',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Allowed: PDF, JPEG, PNG, WEBP, Excel');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadAndProcess = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Please select a file and document type');
      return;
    }
    setUploading(true);
    try {
      const uploadRes = await aiDocumentsAPI.upload(selectedFile, documentType, customerId || null);
      const docId = uploadRes.data?.document_id;
      const inquiryId = uploadRes.data?.inquiry_id;
      toast.success('Document uploaded successfully');
      setUploading(false);
      setProcessing(true);
      const processRes = await aiDocumentsAPI.process(docId);
      if (processRes.data?.status === 'completed') {
        toast.success(`Document processed! Found ${processRes.data?.parts_count || processRes.data?.items_count || 0} items`);
        loadDocuments();
        loadDraftParts();
        loadDraftOrders();
        if (documentType === 'inquiry') {
          toast.success('Inquiry created and items extracted');
          navigate(`/inquiries/${inquiryId}`);
          return;
        } else {
          setActiveTab('draft-orders');
        }
      } else {
        toast.info('Document uploaded. Processing in background.');
      }
      setSelectedFile(null);
      setDocumentType('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  // Draft Part Actions
  const handleEditDraftPart = (draft) => {
    setEditingDraftPart({ ...draft });
  };

  const handleSaveDraftPart = async () => {
    if (!editingDraftPart) return;
    try {
      await aiDocumentsAPI.updateDraftPart(editingDraftPart.id, editingDraftPart);
      toast.success('Draft part updated');
      setEditingDraftPart(null);
      loadDraftParts();
    } catch (err) {
      toast.error('Failed to update draft part');
    }
  };

  const openSmartCopyDialog = async (draft) => {
    setSmartCopyDraft(draft);
    setSelectedFlowToCopy(null);
    setSmartCopySuggestions([]);
    setSmartCopyDialog(true);
    setLoadingSuggestions(true);
    try {
      const res = await aiDocumentsAPI.getDraftPart(draft.id);
      setSmartCopySuggestions(res.data?.process_flow_suggestions || []);
    } catch (err) {
      toast.error('Failed to load process flow suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleApproveDraftPart = async (draftId, copyProcessFlowId = null) => {
    setApproving(true);
    try {
      const res = await aiDocumentsAPI.approveDraftPart(draftId, { copy_process_flow_id: copyProcessFlowId });
      const partId = res.data?.part_id;
      const prefillData = res.data?.prefill_process_flow;
      setSmartCopyDialog(false);
      setSmartCopyDraft(null);
      if (copyProcessFlowId && prefillData && partId) {
        toast.success('Part created! Redirecting to Process Flow Builder to review and save the flow...');
        sessionStorage.setItem('smartCopyPrefill', JSON.stringify({
          partId, sourceFlowId: prefillData.source_flow_id,
          sourcePartNo: prefillData.source_part_no,
          steps: prefillData.steps, netWeightGrams: prefillData.net_weight_grams
        }));
        setTimeout(() => navigate(`/process-flows/new?part=${partId}&smartCopy=true`), 1000);
      } else {
        toast.success(res.data?.message || 'Draft part approved');
        loadDraftParts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve draft part');
    } finally {
      setApproving(false);
    }
  };

  const handleQuickApproveDraftPart = async (draft) => {
    if (draft.matched_existing_part_id) {
      await handleApproveDraftPart(draft.id);
      return;
    }
    openSmartCopyDialog(draft);
  };

  const handleRejectDraftPart = async (draftId) => {
    try {
      await aiDocumentsAPI.rejectDraftPart(draftId, { reason: 'Rejected by user' });
      toast.success('Draft part rejected');
      loadDraftParts();
    } catch (err) {
      toast.error('Failed to reject draft part');
    }
  };

  // Draft Order Actions
  const handleApproveDraftOrder = async (draftId) => {
    try {
      const res = await aiDocumentsAPI.approveDraftOrder(draftId);
      toast.success(res.data?.message || 'Sales Order created!');
      loadDraftOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve draft order');
    }
  };

  const handleRejectDraftOrder = async (draftId) => {
    try {
      await aiDocumentsAPI.rejectDraftOrder(draftId, { reason: 'Rejected by user' });
      toast.success('Draft order rejected');
      loadDraftOrders();
    } catch (err) {
      toast.error('Failed to reject draft order');
    }
  };

  const openQuickCreate = (draftOrderId, item) => {
    setSelectedDraftOrder(draftOrders.find(d => d.id === draftOrderId));
    setQuickCreateItemId(item.id);
    setQuickCreateData({
      internal_part_no: '',
      description: item.description || '',
      part_type: 'Bolt'
    });
    setQuickCreateDialog(true);
  };

  const handleQuickCreatePart = async () => {
    if (!selectedDraftOrder || !quickCreateItemId) return;
    try {
      const res = await aiDocumentsAPI.quickCreatePart(selectedDraftOrder.id, quickCreateItemId, quickCreateData);
      toast.success(res.data?.message || 'Part created and linked');
      setQuickCreateDialog(false);
      loadDraftOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create part');
    }
  };

  // Status and icon helpers
  const getStatusBadge = (status) => {
    const map = {
      pending: <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>,
      processing: <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Processing</Badge>,
      completed: <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Completed</Badge>,
      failed: <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>,
      approved: <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Approved</Badge>,
      rejected: <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Rejected</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (mimeType?.includes('image')) return <FileImage className="h-5 w-5 text-blue-500" />;
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            AI Document Processing
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload customer inquiries and purchase orders for automatic data extraction
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadDraftParts(); loadDraftOrders(); loadDocuments(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Documents', count: documents.length, icon: FileText, colors: 'from-blue-50 to-white border-blue-100', iconColor: 'text-blue-500' },
          { label: 'Pending Parts', count: draftParts.length, icon: Package, colors: 'from-yellow-50 to-white border-yellow-100', iconColor: 'text-yellow-500' },
          { label: 'Pending Orders', count: draftOrders.length, icon: ShoppingCart, colors: 'from-orange-50 to-white border-orange-100', iconColor: 'text-orange-500' },
          { label: 'Processed Today', count: documents.filter(d => {
            const uploaded = new Date(d.uploaded_at);
            return uploaded.toDateString() === new Date().toDateString() && d.processing_status === 'completed';
          }).length, icon: CheckCircle, colors: 'from-green-50 to-white border-green-100', iconColor: 'text-green-500' },
        ].map(({ label, count, icon: Icon, colors, iconColor }) => (
          <Card key={label} className={`bg-gradient-to-br ${colors}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                <Icon className={`h-8 w-8 ${iconColor} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="upload" className="gap-2"><Upload className="h-4 w-4" /> Upload</TabsTrigger>
          <TabsTrigger value="documents" className="gap-2"><FileText className="h-4 w-4" /> Documents</TabsTrigger>
          <TabsTrigger value="draft-parts" className="gap-2 relative">
            <Package className="h-4 w-4" /> Draft Parts
            {draftParts.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {draftParts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="draft-orders" className="gap-2 relative">
            <ShoppingCart className="h-4 w-4" /> Draft Orders
            {draftOrders.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {draftOrders.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <DocumentUploadTab
            selectedFile={selectedFile}
            documentType={documentType}
            setDocumentType={setDocumentType}
            uploading={uploading}
            processing={processing}
            onFileSelect={handleFileSelect}
            onUploadAndProcess={handleUploadAndProcess}
            getFileIcon={getFileIcon}
            customers={customers}
            customerId={customerId}
            setCustomerId={setCustomerId}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <DocumentsListTab
            documents={documents}
            loadingDocs={loadingDocs}
            getStatusBadge={getStatusBadge}
            getFileIcon={getFileIcon}
          />
        </TabsContent>

        <TabsContent value="draft-parts" className="space-y-4">
          <DraftPartsTab
            draftParts={draftParts}
            loadingDraftParts={loadingDraftParts}
            editingDraftPart={editingDraftPart}
            setEditingDraftPart={setEditingDraftPart}
            onEditDraftPart={handleEditDraftPart}
            onSaveDraftPart={handleSaveDraftPart}
            onQuickApproveDraftPart={handleQuickApproveDraftPart}
            onRejectDraftPart={handleRejectDraftPart}
          />
        </TabsContent>

        <TabsContent value="draft-orders" className="space-y-4">
          <DraftOrdersTab
            draftOrders={draftOrders}
            onRejectDraftOrder={handleRejectDraftOrder}
            onApproveDraftOrder={handleApproveDraftOrder}
            onOpenQuickCreate={openQuickCreate}
          />
        </TabsContent>
      </Tabs>

      {/* Quick Create Part Dialog */}
      <QuickCreatePartDialog
        open={quickCreateDialog}
        onOpenChange={setQuickCreateDialog}
        data={quickCreateData}
        setData={setQuickCreateData}
        onConfirm={handleQuickCreatePart}
      />

      {/* Smart Copy Dialog */}
      <SmartCopyDialog
        open={smartCopyDialog}
        onOpenChange={setSmartCopyDialog}
        draft={smartCopyDraft}
        suggestions={smartCopySuggestions}
        selectedFlowToCopy={selectedFlowToCopy}
        setSelectedFlowToCopy={setSelectedFlowToCopy}
        loadingSuggestions={loadingSuggestions}
        approving={approving}
        onApprove={handleApproveDraftPart}
      />
    </div>
  );
}
